import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Icon } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import { SettingPageEmployeesSettingFragment } from 'generated/resources/graphql'

import DurationInput from 'modules/core/components/DurationInput'
import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { categoryNameWithTimeTypeLabel } from 'modules/attendance/components/CustomTimeRanges/Content/utils/categoryNameWithTimeTypeLabel'
import ManageSettingVersionModal from 'modules/attendance/components/SettingItemPage/TimeTracking/ManageSettingVersionModal'
import useFetchData from 'modules/attendance/components/SettingItemPage/TimeTracking/TimeTrackingPolicy/useFetchData'
import {
  CustomTimeRangeCategory,
  useLazyFetchCustomTimeRangeCategories,
} from 'modules/attendance/hooks/useFetchCustomTimeRangeCategories'
import EditAttendanceCustomTimeRangeCategoriesForm, {
  EditCustomTimeRangeCategoryFormType,
} from 'modules/attendance/lib/forms/EditAttendanceCustomTimeRangeCategoryForm'
import { useFetchTimeSettingsCustomTimeRangeCategories } from 'modules/time_categories_settings/hooks/useFetchTimeSettingsCustomTimeRangeCategories'

interface Props {
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  category: CustomTimeRangeCategory
}

const EditCategoryModal = ({ setModalOpen, category }: Props) => {
  const [openEffectiveAtFormModal, setOpenEffectiveAtFormModal] = React.useState(false)
  const { currentSetting } = useFetchData(category.attendanceEmployeesSettingId)
  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()
  const { categories: timeSettingsCategories } =
    useFetchTimeSettingsCustomTimeRangeCategories(currentCompanyId)
  const { fetch: fetchLazyCustomTimeRangeCategories } = useLazyFetchCustomTimeRangeCategories()

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const form: FormikProps<EditCustomTimeRangeCategoryFormType> =
    EditAttendanceCustomTimeRangeCategoriesForm({
      category,
    })

  const formReadyToSubmit = form.values.id !== category.id

  const onCreateSetting = async (newSetting: SettingPageEmployeesSettingFragment) => {
    const settingId = newSetting.id
    const newSettingsCustomTimeRangeCategories = await fetchLazyCustomTimeRangeCategories(settingId)

    const newCategory = newSettingsCustomTimeRangeCategories.customTimeRangeCategories.find(
      (newSettingCategory) =>
        newSettingCategory.timeSettingsCustomTimeRangeCategoryId ===
        category.timeSettingsCustomTimeRangeCategoryId
    )

    const attrs = {
      id: newCategory!.id,
      attendanceEmployeesSettingId: settingId,
      rawMinutesInCents: form.values.rawMinutesInCents,
      equivalentMinutesInCents: form.values.equivalentMinutesInCents,
    }

    form.setValues(attrs)
  }

  useEffect(() => {
    if (formReadyToSubmit) {
      form.submitForm()

      const routeParams = {
        id: 'time_tracking',
        settingId: form.values.attendanceEmployeesSettingId.toString(),
        tabId: 'time_ranges',
      }
      navigate(navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams))
    }
  }, [formReadyToSubmit])

  const onSubmitCategoryModal = () => {
    if (!form.dirty) return onClose()

    setOpenEffectiveAtFormModal(true)
  }

  const onClose = () => {
    form.resetForm()
    setModalOpen(false)
  }

  const onUpdate = () => {
    form.submitForm()
    setModalOpen(false)
  }

  const timeSettingsCategory = timeSettingsCategories.customTimeRangeCategories.find(
    (timeSettingsCategory) =>
      timeSettingsCategory.id === category.timeSettingsCustomTimeRangeCategoryId
  )

  if (!timeSettingsCategory)
    throw i18n.t(
      'time_tracking.settings.time_blocks.worked_time_categories.time_settings_category_not_found'
    )

  const placeholder = categoryNameWithTimeTypeLabel(
    timeSettingsCategory.name,
    timeSettingsCategory.timeType
  )

  return (
    <>
      <Modal
        title={i18n.t('time_tracking.settings.time_blocks.worked_time_categories.edit_value')}
        illustration="monety-bag"
        description={''}
        onSubmit={onSubmitCategoryModal}
        onClose={onClose}
        footer={
          <>
            <Button type="secondary" onClick={onClose}>
              {i18n.t('common.cancel')}
            </Button>
            <Button disabled={form.isSubmitting} submit>
              {i18n.t('common.save')}
            </Button>
          </>
        }
      >
        <Box gap="s16">
          <TextField
            label={i18n.t('time_tracking.settings.time_blocks.categories.modals.category')}
            placeholder={placeholder}
            readonly
          />
          <Box flexDirection="row" alignItems="center" justifyContent="spaceEvenly" gap="s8">
            <Box width="full">
              <DurationInput
                label={i18n.t('time_tracking.settings.time_blocks.categories.modals.raw_hours')}
                minutes={form.values.rawMinutesInCents}
                onChange={(value: number) => form.setFieldValue('rawMinutesInCents', value)}
              />
            </Box>
            <Box paddingTop="s24" alignItems="center" width="fit-content">
              <Icon.Medium icon="arrow-right" />
            </Box>
            <Box width="full">
              <DurationInput
                label={i18n.t(
                  'time_tracking.settings.time_blocks.categories.modals.equivalent_hours'
                )}
                minutes={form.values.equivalentMinutesInCents}
                onChange={(value: number) => form.setFieldValue('equivalentMinutesInCents', value)}
              />
            </Box>
          </Box>
        </Box>
      </Modal>
      {openEffectiveAtFormModal && (
        <ManageSettingVersionModal
          onCreate={onCreateSetting}
          onUpdate={onUpdate}
          onClose={onClose}
          running={false}
          attendanceSettingId={currentSetting?.attributes.id}
          attendanceSettingEffectiveAt={currentSetting?.attributes.effective_at}
        />
      )}
    </>
  )
}

export default EditCategoryModal
